import { MutationCache, QueryCache, type Mutation } from "@tanstack/react-query";
import { getTelemetryRoute, startUserAction, trackEvent } from "./telemetry";

export interface TelemetryCachesOptions {
  /** Extracts the HTTP status from an API error. */
  statusOf: (error: unknown) => number | undefined;
  /** Actions reported with `critical` importance, e.g. payments. */
  criticalAction?: RegExp;
}

const durationOf = (mutation: Mutation<unknown, unknown, unknown>) =>
  mutation.state.submittedAt ? Date.now() - mutation.state.submittedAt : undefined;
const idOf = (data: unknown) =>
  data && typeof data === "object" && "id" in data && typeof data.id === "string" ? data.id : undefined;
const actionOf = (mutation: Mutation<unknown, unknown, unknown>) => {
  const action: unknown = mutation.meta?.["action"];
  return typeof action === "string" ? action : undefined;
};
// Leading static parts such as ["client", "list"]; IDs and filters are left out.
const queryName = (key: readonly unknown[]) => {
  const parts: string[] = [];
  for (const part of key.slice(0, 2)) {
    if (typeof part !== "string" || !/^[A-Za-z_-]+$/.test(part)) break;
    parts.push(part);
  }
  return parts.join(".") || "unknown";
};

/** Query client caches that report tagged key actions (`meta.action`) as events and Faro user actions, plus failed data loads. */
export function createTelemetryCaches({ statusOf, criticalAction }: TelemetryCachesOptions) {
  return {
    mutationCache: new MutationCache({
      onMutate(_variables, mutation) {
        const action = actionOf(mutation);
        if (!action) return;
        startUserAction(action, { trigger: "mutation", critical: criticalAction?.test(action) });
      },
      onSuccess(data, _variables, _result, mutation) {
        const action = actionOf(mutation);
        if (!action) return;
        trackEvent("action.succeeded", {
          action, route: getTelemetryRoute(), entityId: idOf(data), durationMs: durationOf(mutation),
        });
      },
      onError(error, _variables, _result, mutation) {
        const action = actionOf(mutation);
        if (!action) return;
        trackEvent("action.failed", {
          action, route: getTelemetryRoute(), status: statusOf(error), durationMs: durationOf(mutation),
        });
      },
    }),
    queryCache: new QueryCache({
      onError(error, query) {
        trackEvent("data.load_failed", {
          query: queryName(query.queryKey), route: getTelemetryRoute(), status: statusOf(error),
        });
      },
    }),
  };
}
